import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery, useAction } from "wasp/client/operations";
import { getSocialReport, renderReportImage } from "wasp/client/operations";
import { ArrowLeft, Share2 } from "lucide-react";
import { Button } from "../client/components/ui/button";
import { Card, CardContent } from "../client/components/ui/card";
import useTranslation from "../client/hooks/useTranslation";
import type { ReportMetrics, ReportPeriod } from "./metrics/types";
import { SummarySection } from "./components/detail/SummarySection";
import { LifecycleSection } from "./components/detail/LifecycleSection";
import { PayoutsSection } from "./components/detail/PayoutsSection";
import { PropFirmFinanceSection } from "./components/detail/PropFirmFinanceSection";
import { AdvancementSection } from "./components/detail/AdvancementSection";

export default function ReportDetailPage() {
  const t = useTranslation();
  const { reportId } = useParams<{ reportId: string }>();
  const { data: report, isLoading, error } = useQuery(getSocialReport, { id: reportId ?? "" });

  const renderAction = useAction(renderReportImage);
  const [isSharing, setIsSharing] = useState(false);
  const [signedUrls, setSignedUrls] = useState<string[] | null>(null);

  async function handleShare() {
    if (!reportId) return;
    setIsSharing(true);
    try {
      const res = await renderAction({ id: reportId });
      setSignedUrls(res.signedUrls);
    } catch {
      // L'erreur de rendu ne doit pas masquer les sections déjà lisibles :
      // on retombe simplement sur l'absence d'image.
      setSignedUrls(null);
    } finally {
      setIsSharing(false);
    }
  }

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="text-sm text-muted-foreground">{t.reports.generating}</div>
      </div>
    );
  }

  if (error || !report) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-6 space-y-4">
        <BackLink label={t.reports.detailBackToList} />
        <div className="text-sm text-destructive">{t.reports.error}</div>
      </div>
    );
  }

  const metrics = report.metricsSnapshot as unknown as ReportMetrics;
  // Absence de schemaVersion === 2 : snapshot v1, les quatre sections v2 y sont
  // absentes et se masquent d'elles-mêmes (elles retournent null).
  const isLegacy = metrics.schemaVersion !== 2;

  const periodLabels: Record<string, string> = {
    monthly: t.reports.periodMonthly,
    quarterly: t.reports.periodQuarterly,
    annual: t.reports.periodAnnual,
  };
  const periodLabel = periodLabels[report.period as ReportPeriod] ?? null;
  const createdAt = new Date(report.createdAt).toLocaleDateString();

  return (
    <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-1">
          <BackLink label={t.reports.detailBackToList} />
          <h1 className="text-2xl font-bold text-foreground">{t.reports.detailTitle}</h1>
          <p className="text-sm text-muted-foreground">
            {periodLabel && <span>{periodLabel} · </span>}
            <span>
              {t.reports.detailGeneratedOn} {createdAt}
            </span>
          </p>
          {metrics.scopeLabel && (
            <p className="text-sm text-muted-foreground">{metrics.scopeLabel}</p>
          )}
        </div>

        <Button onClick={handleShare} disabled={isSharing} size="default">
          <Share2 className="h-4 w-4 mr-2" />
          {isSharing ? t.reports.detailSharing : t.reports.detailShare}
        </Button>
      </div>

      {isLegacy && (
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground">{t.reports.detailLegacyNotice}</p>
          </CardContent>
        </Card>
      )}

      <SummarySection metrics={metrics} />
      <LifecycleSection metrics={metrics} />
      <PropFirmFinanceSection metrics={metrics} />
      <PayoutsSection metrics={metrics} />
      <AdvancementSection metrics={metrics} />

      {signedUrls && signedUrls.length > 0 && (
        <div className="grid grid-cols-1 gap-4 place-items-center">
          {signedUrls.map((url, i) => (
            <img
              key={i}
              src={url}
              alt={t.reports.thumbnailAlt}
              className="block max-w-full h-auto rounded border border-border"
              style={{ maxHeight: "70vh", objectFit: "contain" }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function BackLink({ label }: { label: string }) {
  return (
    <Link
      to="/reports"
      className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
    >
      <ArrowLeft className="h-4 w-4" />
      {label}
    </Link>
  );
}
